// Run page - Audio recording y acciones rápidas

import Alpine from 'alpinejs';
import Swal from 'sweetalert2';

Alpine.data('runPage', (meetingId: string) => ({
  meeting: null as { id: string; title: string } | null,
  isRecording: false,
  isPaused: false,
  currentTime: 0,
  mediaRecorder: null as MediaRecorder | null,
  audioChunks: [] as Blob[],
  segmentSeq: 0,

  async init() {
    // Load meeting from IndexedDB (placeholder)
    this.meeting = { id: meetingId, title: 'Reunión de ejemplo' };
    
    // Initialize MediaRecorder
    await this.initMediaRecorder();
    
    // Update current time every second
    setInterval(() => {
      if (this.isRecording && !this.isPaused) {
        this.currentTime += 1000;
      }
    }, 1000);
  },

  async initMediaRecorder() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.onstop = async () => {
        await this.saveAudioSegment();
      };
    } catch (error) {
      console.error('Error inicializando MediaRecorder:', error);
      Swal.fire('Error', 'No se pudo acceder al micrófono', 'error');
    }
  },

  async startRecording() {
    if (!this.mediaRecorder) {
      Swal.fire('Error', 'Micrófono no disponible', 'error');
      return;
    }
    
    this.isRecording = true;
    this.isPaused = false;
    this.currentTime = 0;
    this.segmentSeq = 0;
    
    this.mediaRecorder.start(10000); // 10 second chunks
    
    // Save start event
    await this.saveEvent({
      event_type: 'start',
      t_ms: 0,
      payload: {},
    });
  },

  async pauseRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;
    
    this.isPaused = !this.isPaused;
    
    if (this.isPaused) {
      this.mediaRecorder.pause();
      await this.saveEvent({
        event_type: 'pause',
        t_ms: this.currentTime,
        payload: {},
      });
    } else {
      this.mediaRecorder.resume();
      await this.saveEvent({
        event_type: 'resume',
        t_ms: this.currentTime,
        payload: {},
      });
    }
  },

  async stopRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;
    
    const result = await Swal.fire({
      title: '¿Finalizar reunión?',
      text: 'Esto detendrá la grabación y marcará la reunión como finalizada.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Finalizar',
      cancelButtonText: 'Cancelar',
    });
    
    if (!result.isConfirmed) return;
    
    this.mediaRecorder.stop();
    this.isRecording = false;
    
    // Save end_meeting event
    await this.saveEvent({
      event_type: 'end_meeting',
      t_ms: this.currentTime,
      payload: {
        paused_duration_ms: 0, // Calculate from pause events
        final_seq: this.segmentSeq,
      },
    });
    
    Swal.fire('¡Completado!', 'Reunión finalizada correctamente', 'success');
    
    // Navigate to review
    setTimeout(() => {
      window.location.href = `/review/${meetingId}`;
    }, 1500);
  },

  async saveAudioSegment() {
    if (this.audioChunks.length === 0) return;
    
    const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
    this.audioChunks = [];
    
    // Save to IndexedDB and enqueue for sync (placeholder)
    console.log(`Saving audio segment ${this.segmentSeq}:`, blob.size, 'bytes');
    
    this.segmentSeq++;
  },

  async saveEvent(event: any) {
    // Save event to IndexedDB and enqueue (placeholder)
    console.log('Saving event:', event);
  },

  async addTask() {
    const result = await Swal.fire({
      title: 'Nueva Tarea',
      html: `
        <input id="task-title" class="swal2-input" placeholder="Título">
        <textarea id="task-desc" class="swal2-textarea" placeholder="Descripción"></textarea>
        <input id="task-assigned" class="swal2-input" placeholder="Asignado a">
      `,
      showCancelButton: true,
      confirmButtonText: 'Crear',
      preConfirm: () => {
        return {
          title: (document.getElementById('task-title') as HTMLInputElement).value,
          description: (document.getElementById('task-desc') as HTMLTextAreaElement).value,
          assigned_to: (document.getElementById('task-assigned') as HTMLInputElement).value,
        };
      },
    });

    if (result.isConfirmed && result.value.title) {
      await this.saveEvent({
        event_type: 'task',
        t_ms: this.currentTime,
        payload: result.value,
      });
      
      Swal.fire('Tarea creada', '', 'success');
    }
  },

  async addDecision() {
    const result = await Swal.fire({
      title: 'Nueva Decisión',
      html: `
        <input id="dec-title" class="swal2-input" placeholder="Título">
        <textarea id="dec-desc" class="swal2-textarea" placeholder="Descripción"></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      preConfirm: () => {
        return {
          title: (document.getElementById('dec-title') as HTMLInputElement).value,
          description: (document.getElementById('dec-desc') as HTMLTextAreaElement).value,
        };
      },
    });

    if (result.isConfirmed && result.value.title) {
      await this.saveEvent({
        event_type: 'decision',
        t_ms: this.currentTime,
        payload: result.value,
      });
      
      Swal.fire('Decisión registrada', '', 'success');
    }
  },

  formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  },
}));

